/**
 * Presentation helpers. Pure, so they are trivially testable.
 *
 * Every number whose *punctuation* depends on locale — sizes and percentages —
 * goes through `Intl` with an explicit `pt-BR` locale rather than through
 * `toFixed`/`toLocaleString`. The locale is pinned on purpose: the UI is
 * Brazilian Portuguese regardless of what the browser is set to, so "7,3 MB"
 * must not become "7.3 MB" for someone whose OS is in English. A timecode is
 * not one of them, so `formatDuration` stays plain string work.
 *
 * The unit symbols (B, kB, MB, GB) are international and stay put —
 * `Intl`'s own `style: 'unit'` spells the smallest one out as "bytes", which is
 * wider and noisier than the symbol every file manager already shows.
 */

/** Formatter cache: constructing an `Intl.NumberFormat` is not cheap. */
const numberFormats = new Map<string, Intl.NumberFormat>()

function nf(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options)
  let formatter = numberFormats.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat('pt-BR', options)
    numberFormats.set(key, formatter)
  }
  return formatter
}

/** 213 → "3:33", 3723 → "1:02:03". Negative/NaN input degrades to "0:00". */
export function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '0:00'
  const seconds = Math.floor(totalSeconds)
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const rest = seconds % 60

  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(rest)}`
  }
  return `${minutes}:${pad(rest)}`
}

/** 7340032 → "7,0 MB". Uses decimal units, like every file manager users see. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'kB', 'MB', 'GB'] as const
  let value = bytes
  let unitIndex = 0
  while (value >= 1000 && unitIndex < units.length - 1) {
    value /= 1000
    unitIndex += 1
  }
  const unit = units[unitIndex] ?? 'B'
  const decimals = unitIndex === 0 || value >= 100 ? 0 : 1
  const formatted = nf({
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
  return `${formatted} ${unit}`
}

/** 42.53 → "43%" — whole numbers only; a jittering decimal reads as noise. */
export function formatPercent(percent: number): string {
  if (!Number.isFinite(percent)) return '0%'
  const clamped = Math.max(0, Math.min(100, Math.round(percent)))
  return nf({ style: 'percent', maximumFractionDigits: 0 }).format(clamped / 100)
}

/**
 * Two digits: the "33" in "3:33".
 *
 * Not an `Intl` call. A timecode is the one number here that is not
 * locale-sensitive — `1:02:03` is `1:02:03` everywhere — so it needs no
 * separators, no grouping and no formatter.
 */
function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value)
}
